import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Sparkles, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

export default function AIPracticeScenario({ pageTitle, pageNumber, chapterId, chapterTitle, context }) {
  // Support both prop patterns
  const effectiveContext = context || pageTitle || chapterTitle;
  const effectivePageNumber = pageNumber || chapterId || 0;
  
  const [scenario, setScenario] = useState('');
  const [userResponse, setUserResponse] = useState('');
  const [generatedScenario, setGeneratedScenario] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateScenario = async () => {
    setIsGenerating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/generate-scenario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          context: effectiveContext,
          page_number: effectivePageNumber 
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeneratedScenario(data.scenario);
        toast.success('Practice scenario generated!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to generate scenario');
      }
    } catch (error) {
      console.error('Scenario generation error:', error);
      toast.error('Error connecting to server. Please try again.');
    }
    setIsGenerating(false);
  };

  const saveToLore = async () => {
    const userId = localStorage.getItem('workbook_user_id');
    if (!userId) {
      toast.error('Please log in to save');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lore/save-practice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          page_title: effectiveContext,
          page_number: effectivePageNumber,
          scenario: generatedScenario || scenario,
          response: userResponse,
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        toast.success('Saved to your LORE library!');
        setScenario('');
        setUserResponse('');
        setAiScenario(null);
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Error saving to LORE');
    }
  };

  return (
    <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border border-purple-700/30 rounded-lg p-6 mt-8">
      <div className="flex items-center gap-3 mb-4">
        <Sparkles className="h-6 w-6 text-purple-400" />
        <h3 className="text-xl font-bold text-white">Practice Scenario Generator</h3>
      </div>
      
      <p className="text-purple-100 mb-4 text-sm">
        Generate a practice scenario based on this content, or write your own.
      </p>

      <div className="space-y-4">
        {/* Generate Scenario */}
        <div>
          <Button 
            onClick={generateScenario}
            disabled={isGenerating}
            className="bg-purple-600 hover:bg-purple-500 text-white w-full mb-3"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {isGenerating ? 'Generating...' : 'Generate Practice Scenario'}
          </Button>
          
          {generatedScenario && (
            <div className="bg-purple-900/40 border border-purple-700/40 rounded p-4 mb-3">
              <p className="text-purple-100 leading-relaxed">{generatedScenario}</p>
            </div>
          )}
        </div>

        {/* Manual Scenario */}
        <div>
          <label className="text-purple-200 text-sm mb-2 block">Or create your own scenario:</label>
          <Textarea 
            value={scenario}
            onChange={(e) => setScenario(e.target.value)}
            placeholder="Describe a situation you'd like to practice..."
            className="bg-purple-900/20 border-purple-700/30 text-white placeholder:text-purple-300/40 min-h-[80px]"
          />
        </div>

        {/* User Response */}
        <div>
          <label className="text-purple-200 text-sm mb-2 block">Your NVC Response:</label>
          <Textarea 
            value={userResponse}
            onChange={(e) => setUserResponse(e.target.value)}
            placeholder="Practice your OFNR response here..."
            className="bg-purple-900/20 border-purple-700/30 text-white placeholder:text-purple-300/40 min-h-[100px]"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button onClick={saveToLore} className="bg-green-600 hover:bg-green-500 text-white flex-1">
            <Save className="h-4 w-4 mr-2" />
            Save to LORE Library
          </Button>
          <Button onClick={() => { setScenario(''); setUserResponse(''); setGeneratedScenario(null); }} variant="outline" className="border-purple-600 text-purple-200 hover:bg-purple-900/20">
            <RotateCcw className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
}
